import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "../ui/Button"
import { Dialog, DialogProps } from "../ui/Dialog"
import styleReadyPngUrl from "@assets/style_ready.png"
import { clsx } from "clsx"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"

export const StyleNamingDialog = (
	props: DialogProps & {
		initialName: string
		onCompleteWithError: (result: { name: string }) => string | void | Promise<string | void>
		title: string
		actionTitle: string
		description?: string
		subTitle?: string
		errorMessage?: string
	},
) => {
	const { t } = useTranslation("components")
	const [name, setName] = useState(props.initialName)
	const [error, setError] = useState<string | null>(props.errorMessage || null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	useEffect(() => {
		setError(props.errorMessage || null)
	}, [props.errorMessage])

	const submitName = async () => {
		setIsSubmitting(true)
		try {
			const validationError = await props.onCompleteWithError({
				name: name.trim(),
			})
			if (validationError) {
				setError(validationError)
			}
		} catch (error) {
			console.error("Failed to complete style naming", error)
			popActiveLoadingDialogs()
			setError(t("styleNamingDialog.errorSaveStyle"))
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<Dialog
			contentSize="md"
			{...props}
			bottomButtons={
				<div className={"flex w-full flex-1 flex-row items-center justify-end space-x-2"}>
					<Button
						trackingLabel="confirm"
						aria-label={t("styleNamingDialog.ariaConfirmStyleNaming")}
						variant="primary"
						disabled={!name.trim() || isSubmitting}
						onClick={() => {
							void submitName()
						}}
					>
						{props.actionTitle}
					</Button>
				</div>
			}
		>
			<div className="flex size-full flex-col space-y-4 overflow-hidden">
				<div className="flex w-full flex-col items-start space-y-2">
					<div className="text-md font-bold">{props.title}</div>
					{props.description && (
						<div className="text-sm text-base-400">{props.description}</div>
					)}
				</div>

				<div className="flex h-full min-h-0 flex-col items-center">
					<div className="flex max-h-[80%] items-center justify-center overflow-hidden">
						<img className="max-h-full object-contain" src={styleReadyPngUrl} />
					</div>
					{props.subTitle && (
						<div className="shrink-0 pb-2 text-sm">{props.subTitle}</div>
					)}
					<input
						className={clsx(
							"w-1/3 rounded-md bg-base-700 px-2 py-1 text-sm outline-4",
							error ? "outline-red-500" : "outline-base-600",
						)}
						aria-label={t("styleNamingDialog.ariaNewStyleName")}
						type="text"
						data-testid="input-style-name"
						value={name}
						onChange={(e) => {
							setName(e.target.value)
							if (error) setError(null)
						}}
					/>
					<div className="mt-2 min-h-5 shrink-0 text-sm text-red-400">{error}</div>
				</div>
			</div>
		</Dialog>
	)
}
